import CheckIcon from "@mui/icons-material/Check";
import RemoveIcon from "@mui/icons-material/Remove";
import { Checkbox as MuiCheckbox, styled } from "@mui/material";
import clsx from "clsx";
import React from "react";
const classes = {
    root: "checkbox-root",
    container: "checkbox-container",
    checkbox: "checkbox-checkbox",
    checkedCheckbox: "checkbox-checkedCheckbox",
    checkedIcon: "checkbox-checkedIcon",
    disabledLabel: "checkbox-disabledLabel"
};
//The styled approach is being used here as it is the path of least resistance when retaining classes(mui) styles that already exist
//However, this is not the approach we want for new components if it is avoidable.
//Instead, we want to use material's sx prop OR emotion's css styling https://emotion.sh/docs/css-prop to create style objects
//If you are going to use styled on a wrapper component, make sure that it is defined OUTSIDE of the function component.
//This is to avoid recreating the div element on the rerender of the component
const StyledDiv = styled("div")(({ compact, theme }) => ({
    [`& .${classes.root}`]: {
        padding: compact ? "4px" : "9px",
        "&:hover": {
            [`& .${classes.checkbox}`]: {
                backgroundColor: theme.palette.background.hover,
                border: `solid 1px ${theme.palette.controls.input.border.hover}`
            },
            [`& .${classes.checkedCheckbox}`]: {
                backgroundColor: theme.palette.background.hover,
                color: theme.palette.controls.input.border.hover,
                [`& .${classes.checkedIcon}`]: {
                    color: theme.palette.controls.input.border.hover
                }
            }
        }
    },
    [`&.${classes.container}`]: {
        alignItems: "center",
        display: "flex",
        margin: compact ? "0px" : "2px"
    },
    [`& .${classes.checkbox}`]: {
        backgroundColor: theme.palette.background.white,
        border: `solid 1px ${theme.palette.controls.input.border.main}`,
        borderRadius: "50%",
        height: "24px",
        width: "25px",
        "input:disabled ~ &": {
            opacity: "0.4"
        },
        "input:focus-visible ~ &": {
            border: `solid 1px ${theme.palette.border.focus}`,
            boxShadow: `0 0 0 3px ${theme.palette.border.main}`
        }
    },
    [`& .${classes.checkedCheckbox}`]: {
        backgroundColor: theme.palette.background.active,
        border: `solid 1px ${theme.palette.border.focus}`,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        "input:disabled ~ &": {
            color: theme.palette.text.white,
            opacity: "0.4"
        }
    },
    [`& .${classes.checkedIcon}`]: {
        color: theme.palette.text.white,
        width: "13px",
        height: "24px",
        borderRadius: "50%"
    },
    [`& .${classes.disabledLabel}`]: {
        color: theme.palette.text.disabled,
        opacity: "0.4"
    },
    [`& .Mui-disabled`]: {
        opacity: "0.4"
    }
}));
/**
 * Custom Checkbox control.
 * @param props @see CheckboxProps
 *
 */
export function Checkbox({ checked, disabled, displayOnly, id, indeterminate, compact, label, onChange, required, value, "aria-label": ariaLabel, style, ...props }) {
    return (React.createElement(StyledDiv, { className: classes.container, style: style, compact: compact },
        React.createElement(MuiCheckbox, { icon: React.createElement("div", { className: classes.checkbox }), checkedIcon: React.createElement("span", { className: clsx(classes.checkbox, classes.checkedCheckbox) },
                React.createElement(CheckIcon, { className: classes.checkedIcon })), checked: checked, disabled: disabled, onChange: (event, checked) => (!displayOnly && onChange ? onChange(event, checked) : undefined), required: required, id: id, indeterminate: indeterminate, indeterminateIcon: React.createElement("span", { className: clsx(classes.checkbox, classes.checkedCheckbox) },
                React.createElement(RemoveIcon, { className: classes.checkedIcon })), inputProps: { "aria-label": ariaLabel }, value: value, disableRipple: true, className: classes.root, "aria-hidden": displayOnly, tabIndex: displayOnly ? -1 : 0, ...props }),
        label && (React.createElement("label", { "aria-hidden": displayOnly, htmlFor: id, className: disabled ? classes.disabledLabel : "" }, label))));
}
//Prop defaults
Checkbox.defaultProps = {
    disabled: false,
    checked: false,
    indeterminate: false,
    required: false,
    compact: false,
    displayOnly: false
};
